import { useEffect, useCallback, useState } from 'react';
import { cartListener } from '~/firebase/listeners/cart';
import initCart from '~/firebase/initialDoc/initCart';

import uidAtom from '~/recoil/atom/uid';
import emailAtom from '~/recoil/atom/email';
import couponAtom from '~/recoil/atom/coupon';
import ordersAtom from '~/recoil/atom/orders';
import taxInfoAtom from '~/recoil/atom/taxInfo';
import taxTypeAtom from '~/recoil/atom/taxType';
import cartTypeAtom from '~/recoil/atom/cartType';
import orderTypeAtom from '~/recoil/atom/orderType';
import cartStatusAtom from '~/recoil/atom/cartStatus';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import campaignIdAtom from '~/recoil/atom/campaignId';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import paymentTypeAtom from '~/recoil/atom/paymentType';
import remotePickMealPointAtom from '~/recoil/atom/remotePickMealPoint';

import authAtom from '~/recoil/atom/auth';
import platformAtom from '~/recoil/atom/platform';
import globalDataStatusAtom from '~/recoil/atom/globalDataStatus';
import displayPickMealPointListSelector from '~/recoil/selectors/displayPickMealPointList';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';

import * as fp from 'lodash/fp';

import { PLATFORM_ID } from '~/globalVariables';

// types
import { SnapshotFn } from '~/firebase/listeners/type';
import { FirebaseDataStatus } from '~/types/utilities';
import { PlatformCartInFirestore } from '~/types/cart';
import { User } from '@firebase/auth';
import { RemotePickMealPointWithId } from '~/types/platform/remotePickMealPoint';
import { PreOrderSetting } from '~/types/common';

export default function useCart() {
  const [cartInit, setCartInit] = useState(false);

  const auth = useRecoilValue(authAtom);
  const [globalDataStatus, setGlobalDataStatus] = useRecoilState(globalDataStatusAtom);
  const platform = useRecoilValue(platformAtom);
  const remotePickMealPointsList = useRecoilValue(displayPickMealPointListSelector);

  const setUid = useSetRecoilState(uidAtom);
  const setEmail = useSetRecoilState(emailAtom);
  const setOrders = useSetRecoilState(ordersAtom);
  const setCoupon = useSetRecoilState(couponAtom);
  const setTaxInfo = useSetRecoilState(taxInfoAtom);
  const setTaxType = useSetRecoilState(taxTypeAtom);
  const setCartType = useSetRecoilState(cartTypeAtom);
  const setOrderType = useSetRecoilState(orderTypeAtom);
  const setCampaignId = useSetRecoilState(campaignIdAtom);
  const setCartStatus = useSetRecoilState(cartStatusAtom);
  const setPickMealAt = useSetRecoilState(pickMealAtAtom);
  const setPickMealEnd = useSetRecoilState(pickMealEndAtom);
  const setPaymentType = useSetRecoilState(paymentTypeAtom);
  const setRemotePickMealPoint = useSetRecoilState(remotePickMealPointAtom);

  const handleSnapShot = useCallback<SnapshotFn>((doc) => {
    const data = doc.data() as PlatformCartInFirestore | undefined;

    if (!doc.exists() || !data) {
      setGlobalDataStatus((prev) => ({ ...prev, cart: FirebaseDataStatus.NOT_EXIST }));
      return;
    }
    setGlobalDataStatus((prev) => ({ ...prev, cart: FirebaseDataStatus.EXIST }));

    setUid(data.uid);
    setEmail(data.email);
    setOrders(data.orders);
    setTaxInfo(data.taxInfo);
    setTaxType(data.taxType);
    setCartType(data.cartType);
    setCartStatus(data.status);
    setOrderType(data.orderType);
    setCoupon(data.coupon || null);
    setPaymentType(data.paymentType);
    setCampaignId(data.campaignId || '');
    setPickMealAt(data.pickMealAt ? data.pickMealAt.toDate() : null);
    setPickMealEnd(data.pickMealEnd ? data.pickMealEnd.toDate() : null);
    setRemotePickMealPoint(data.pickMealPoint);
  }, []);

  useEffect(() => {
    const handleInitCart = async (
      auth: User,
      remotePickMealPointsList: Array<RemotePickMealPointWithId>,
      preOrderSetting: PreOrderSetting,
    ) => {
      try {
        await initCart(PLATFORM_ID, auth, {
          pickMealPointsList: remotePickMealPointsList,
          preOrderSetting,
          allowOrderTypes: platform.platformSetting.allowOrderTypes,
        });
        setCartInit(true);
      } catch (err) {
        console.error('init Cart error');
      }
    };

    if (
      !cartInit &&
      auth &&
      fp.eq(globalDataStatus.platform, FirebaseDataStatus.EXIST) &&
      fp.eq(globalDataStatus.remotePickMealPoints, FirebaseDataStatus.EXIST) &&
      fp.gt(remotePickMealPointsList.length, 0)
    ) {
      handleInitCart(auth, remotePickMealPointsList, platform.preOrderSetting);
    }

    if (cartInit && !auth) {
      setCartInit(false);
    }
  }, [
    cartInit,
    auth,
    globalDataStatus.platform,
    globalDataStatus.remotePickMealPoints,
    platform.preOrderSetting,
    platform.platformSetting.allowOrderTypes,
    remotePickMealPointsList,
  ]);

  useEffect(() => {
    if (cartInit && auth) {
      const unlisten = cartListener(PLATFORM_ID, auth.uid, handleSnapShot);

      return () => {
        unlisten();
        setOrders([]);
        setGlobalDataStatus((prev) => ({ ...prev, cart: FirebaseDataStatus.INIT }));
      };
    }
  }, [auth, cartInit]);
}
